import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDataRefresh } from '../contexts/DataRefreshContext';
import {
  getContas,
  getCartoes,
  getRecorrencias,
  getTransacoes,
  getCategorias,
  getMetas,
  getCartoesAdicionais,
} from '../services/financeService';
import {
  getPortadores,
  getProgramasDoPortador,
  getTransacoesDoPortador,
} from '../services/milhasService';
import { getTelegramVinculo } from '../services/mensagensService';
import {
  isPrimeirosPassosDismissed,
  setPrimeirosPassosDismissed,
  isCategoriasPrimeirosPassosVisto,
  isPrimeirosPassosForced,
  clearPrimeirosPassosForce,
  restorePrimeirosPassos,
} from '../utils/primeirosPassosStorage';

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function hasItems(data) {
  if (typeof data?.count === 'number') return data.count > 0;
  return asArray(data).length > 0;
}

/**
 * Quais módulos o guia mostra: o que a pessoa respondeu no primeiro acesso
 * (preferencias_ui.objetivo), limitado aos módulos que ela tem. Sem resposta, mostra os dois.
 */
export function modulosDoGuia(user, temModuloFinanceiro, temModuloMilhas) {
  const objetivo = user?.preferencias_ui?.objetivo;
  let fin = temModuloFinanceiro && objetivo !== 'milhas';
  let milhas = temModuloMilhas && objetivo !== 'financas';
  if (!fin && !milhas) {
    fin = temModuloFinanceiro;
    milhas = temModuloMilhas;
  }
  return { fin, milhas };
}

/**
 * Checklist de setup inicial derivado dos dados do usuário (sem backend).
 */
export function usePrimeirosPassos() {
  const { user, temModuloFinanceiro: temFin, temModuloMilhas: temMilhas } = useAuth();
  const { fin: temModuloFinanceiro, milhas: temModuloMilhas } = modulosDoGuia(user, temFin, temMilhas);
  const { refreshKey } = useDataRefresh();
  const userId = user?.id ?? user?.pk ?? 'anon';

  const [dismissed, setDismissed] = useState(() => isPrimeirosPassosDismissed(userId));
  const [forced, setForced] = useState(() => isPrimeirosPassosForced(userId));
  const [categoriasVisto, setCategoriasVisto] = useState(() => isCategoriasPrimeirosPassosVisto(userId));
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({
    hasConta: false,
    hasCartao: false,
    hasLancamento: false,
    hasRecorrencia: false,
    hasCategoriaUsuario: false,
    hasMeta: false,
    hasLancamentoCartao: false,
    hasCartaoAdicional: false,
    firstCartaoId: null,
    hasTelegram: false,
    hasPortador: false,
    hasPrograma: false,
    hasMovimentacaoMilhas: false,
  });

  const syncStorageFlags = useCallback(() => {
    setDismissed(isPrimeirosPassosDismissed(userId));
    setForced(isPrimeirosPassosForced(userId));
    setCategoriasVisto(isCategoriasPrimeirosPassosVisto(userId));
  }, [userId]);

  useEffect(() => {
    syncStorageFlags();
  }, [syncStorageFlags]);

  useEffect(() => {
    const onChanged = () => syncStorageFlags();
    window.addEventListener('primeiros-passos-changed', onChanged);
    return () => window.removeEventListener('primeiros-passos-changed', onChanged);
  }, [syncStorageFlags]);

  const load = useCallback(async () => {
    if (!temModuloFinanceiro && !temModuloMilhas) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = {
        hasConta: false,
        hasCartao: false,
        hasLancamento: false,
        hasRecorrencia: false,
        hasCategoriaUsuario: false,
        hasMeta: false,
        hasLancamentoCartao: false,
        hasCartaoAdicional: false,
        firstCartaoId: null,
        hasTelegram: false,
        hasPortador: false,
        hasPrograma: false,
        hasMovimentacaoMilhas: false,
      };

      const tasks = [];

      if (temModuloFinanceiro) {
        tasks.push(
          (async () => {
            const [contas, cartoes, transacoes, recorrencias, categorias, metas, telegram] = await Promise.all([
              getContas('ATIVO').catch(() => []),
              getCartoes().catch(() => []),
              getTransacoes({}).catch(() => []),
              getRecorrencias().catch(() => []),
              getCategorias().catch(() => []),
              getMetas().catch(() => []),
              getTelegramVinculo().catch(() => null),
            ]);
            const cartoesList = asArray(cartoes);
            const cartoesAtivos = cartoesList.filter((c) => c.status !== 'ARQUIVADO');
            const cartoesUteis = cartoesAtivos.length > 0 ? cartoesAtivos : cartoesList;
            const txs = asArray(transacoes);

            next.hasConta = asArray(contas).length > 0;
            next.hasTelegram = Boolean(telegram?.vinculado);
            next.hasCartao = cartoesUteis.length > 0;
            next.hasLancamento = hasItems(transacoes);
            next.hasRecorrencia = asArray(recorrencias).length > 0;
            next.hasCategoriaUsuario = asArray(categorias).some((c) => c.natureza === 'USUARIO');
            next.hasMeta = asArray(metas).length > 0;
            next.hasLancamentoCartao = cartoesUteis.some((c) => c.tem_movimentacao)
              || txs.some((t) => Boolean(t.fatura));

            if (cartoesUteis.length > 0) {
              next.firstCartaoId = cartoesUteis[0].id;
              const adicionais = await getCartoesAdicionais(cartoesUteis[0].id, 'ATIVO').catch(() => []);
              next.hasCartaoAdicional = asArray(adicionais).length > 0;
            }
          })(),
        );
      }

      if (temModuloMilhas) {
        tasks.push(
          (async () => {
            const portadoresRes = await getPortadores().catch(() => ({ data: [] }));
            const portadores = asArray(portadoresRes?.data ?? portadoresRes);
            next.hasPortador = portadores.length > 0;

            const programasRes = await getProgramasDoPortador().catch(() => ({ data: [] }));
            const programas = asArray(programasRes?.data ?? programasRes).filter(
              (p) => !p.status || p.status === 'ATIVO',
            );
            next.hasPrograma = programas.length > 0;

            if (portadores.length > 0) {
              const txsRes = await getTransacoesDoPortador(portadores[0].id).catch(() => ({ data: [] }));
              next.hasMovimentacaoMilhas = asArray(txsRes?.data ?? txsRes).length > 0;
            }
          })(),
        );
      }

      await Promise.all(tasks);
      setStatus(next);
    } finally {
      setLoading(false);
    }
  }, [temModuloFinanceiro, temModuloMilhas]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const steps = useMemo(() => {
    const list = [];
    if (temModuloFinanceiro) {
      list.push(
        {
          id: 'conta',
          label: 'Adicionar uma conta',
          description: 'Cadastre contas bancárias ou carteiras para acompanhar saldos.',
          done: status.hasConta,
          path: '/contas',
          required: true,
        },
        {
          id: 'cartao',
          label: 'Adicionar um cartão',
          description: 'Informe a conta usada geralmente para pagar a fatura.',
          done: status.hasCartao,
          path: '/cartoes',
          required: true,
          blocked: !status.hasConta,
          blockedHint: 'Cadastre uma conta antes.',
        },
        {
          id: 'lancamento',
          label: 'Registrar um lançamento',
          description: 'Lance receitas ou despesas para ver o desempenho.',
          done: status.hasLancamento,
          path: '/transacoes',
          required: true,
          blocked: !status.hasConta,
          blockedHint: 'Cadastre uma conta antes.',
        },
        {
          id: 'telegram',
          label: 'Lançar pelo Telegram',
          description: 'O jeito mais rápido: escreva "mercado 80" ou mande a foto do comprovante e o lançamento entra sozinho.',
          done: status.hasTelegram,
          path: '/configuracoes?aba=integracoes',
          required: false,
          destaque: true,
          blocked: !status.hasConta,
          blockedHint: 'Cadastre uma conta antes.',
        },
        {
          id: 'pix-cobranca',
          label: 'Cadastrar Pix para cobranças',
          description: 'Inclua sua chave Pix nas Configurações para enviar lembretes no WhatsApp.',
          done: Boolean(user?.chave_pix_padrao && String(user.chave_pix_padrao).trim()),
          path: '/configuracoes',
          required: false,
        },
        {
          id: 'perfil-prestador',
          label: 'Você cobra clientes com frequência?',
          description: 'Se sim, liberamos Clientes e A receber. Cobrança ocasional de amigos não precisa disso.',
          done: Boolean(
            user?.modo_prestador_ativo
            || user?.preferencias_ui?.modo_prestador_perfil_respondido,
          ),
          action: 'perfil-prestador',
          required: false,
        },
        {
          id: 'categorias',
          label: 'Gerenciar categorias',
          description: 'Adicione, altere ou remova categorias para organizar seus lançamentos.',
          done: status.hasCategoriaUsuario || categoriasVisto,
          path: '/categorias',
          required: false,
        },
        {
          id: 'recorrencia',
          label: 'Criar uma recorrência',
          description: 'Automatize despesas e receitas mensais.',
          done: status.hasRecorrencia,
          path: '/recorrencias',
          required: false,
        },
        {
          id: 'meta',
          label: 'Criar uma meta',
          description: 'Defina limites por categoria e acompanhe o progresso.',
          done: status.hasMeta,
          path: '/metas',
          required: false,
        },
      );

      if (status.hasCartao) {
        const cartaoPath = status.firstCartaoId
          ? `/cartoes/${status.firstCartaoId}`
          : '/cartoes';
        list.push(
          {
            id: 'lancamento-cartao',
            label: 'Lançar uma compra no cartão',
            description: 'Registre uma despesa na fatura do cartão de crédito.',
            done: status.hasLancamentoCartao,
            path: cartaoPath,
            required: false,
          },
          {
            id: 'cartao-adicional',
            label: 'Criar um cartão adicional',
            description: 'Opcional: vincule um cartão adicional ao cartão principal.',
            done: status.hasCartaoAdicional,
            path: cartaoPath,
            required: false,
          },
        );
      }
    }
    if (temModuloMilhas) {
      list.push(
        {
          id: 'portador',
          label: 'Cadastrar um portador',
          description: 'Quem detém os programas de milhas.',
          done: status.hasPortador,
          path: '/milhas/portadores',
          required: true,
        },
        {
          id: 'programa',
          label: 'Ativar um programa',
          description: 'Na Gestão de Portadores, use Ativar programa na linha da pessoa (Smiles, Latam Pass, Azul etc.).',
          done: status.hasPrograma,
          path: '/milhas/portadores',
          required: true,
          blocked: !status.hasPortador,
          blockedHint: 'Cadastre um portador antes.',
        },
        {
          id: 'movimentacao',
          label: 'Registrar movimentação de milhas',
          description: 'Entradas, saídas ou transferências no extrato.',
          done: status.hasMovimentacaoMilhas,
          path: '/milhas/extrato',
          required: true,
          blocked: !status.hasPrograma,
          blockedHint: 'Ative um programa antes.',
        },
      );
    }
    return list;
  }, [
    temModuloFinanceiro,
    temModuloMilhas,
    status,
    categoriasVisto,
    user?.chave_pix_padrao,
    user?.modo_prestador_ativo,
    user?.preferencias_ui?.modo_prestador_perfil_respondido,
  ]);

  const requiredSteps = steps.filter((s) => s.required);
  const doneRequired = requiredSteps.filter((s) => s.done).length;
  const allRequiredDone = requiredSteps.length > 0 && doneRequired === requiredSteps.length;
  // Menu "Primeiros passos" força exibição mesmo com setup já concluído.
  const visible =
    requiredSteps.length > 0
    && (forced || (!dismissed && !allRequiredDone));

  const dismiss = useCallback(() => {
    setPrimeirosPassosDismissed(userId, true);
    clearPrimeirosPassosForce(userId);
    setDismissed(true);
    setForced(false);
  }, [userId]);

  const restore = useCallback(() => {
    restorePrimeirosPassos(userId);
    setDismissed(false);
    setForced(true);
  }, [userId]);

  return {
    visible,
    loading,
    steps,
    doneRequired,
    totalRequired: requiredSteps.length,
    dismiss,
    restore,
    refresh: load,
  };
}
